import { useEffect, useMemo, useState } from 'react';
import { FaBookOpen, FaBriefcase, FaChartBar, FaLayerGroup, FaLaptopCode, FaShareAlt } from 'react-icons/fa';
import { API_CORE } from '../../../../../_CORE/API/ApiCORE';
import { MetricCard, MetricGrid } from '../../../../../_CORE/APP/Templates/_UI/Metrics/_index';
import { PORTFOLIO_PUBLIC_CONSTANTS } from '../../../Public/_Portfolio/_MODULES/_API';
import { AdminViewLayout } from '../../_Shared/_UI/AdminViewLayout';
import '../../_Shared/_UI/_Styles/PortfolioAdmin.css';

const isVisible = (item) => item?.active !== false;

const countVisible = (items) => (Array.isArray(items) ? items.filter(isVisible).length : 0);

const countFilled = (source, fields) => fields.reduce((total, field) => {
    const value = source?.[field];
    return total + (value !== null && value !== undefined && String(value).trim() !== '' ? 1 : 0);
}, 0);

export const DashboardViewMain = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        let active = true;

        const load = async () => {
            setIsLoading(true);
            try {
                const endpoint = API_CORE.ENDPOINTS[PORTFOLIO_PUBLIC_CONSTANTS.GET];
                const response = await API_CORE.SERVICES.GET_RESOURCE(endpoint);

                if (!active) return;

                if (response?.success) {
                    setData(response.data || {});
                    setError('');
                } else {
                    setError(response?.message || 'No se pudo cargar el resumen.');
                }
            } catch (err) {
                if (active) setError(err?.message || 'No se pudo cargar el resumen.');
            } finally {
                if (active) setIsLoading(false);
            }
        };

        load();
        return () => { active = false; };
    }, []);

    const overview = useMemo(() => {
        const projects = data?.projects ?? [];
        const experience = data?.experience ?? [];
        const blog = data?.blog ?? [];
        const socials = data?.socials ?? [];
        const skills = data?.skills ?? [];
        const stats = data?.stats ?? [];

        const profileFields = ['name', 'role', 'hero_title', 'hero_subtitle', 'location', 'photo'];

        const profileScore = Math.round((countFilled(data?.profile, profileFields) / profileFields.length) * 100);

        return {
            projects,
            experience,
            blog,
            socials,
            skills,
            stats,
            visibleProjects: countVisible(projects),
            visibleExperience: countVisible(experience),
            visibleBlog: countVisible(blog),
            visibleSocials: countVisible(socials),
            visibleSkills: countVisible(skills),
            visibleStats: countVisible(stats),
            featuredProjects: projects.filter((item) => item?.featured === true && isVisible(item)).length,
            featuredPosts: blog.filter((item) => item?.featured === true && isVisible(item)).length,
            profileScore,
        };
    }, [data]);

    return (
        <AdminViewLayout
            pageKey="dashboard"
            title="Panel del portfolio"
            description="Metricas de contenido, publicacion y completitud. La navegacion queda en el sidebar, como en el layout original."
        >
            {error && <div className="PBADMIN__alert PBADMIN__alert--standalone">{error}</div>}

            <MetricGrid columns="auto" gap="lg">
                <MetricCard
                    label="Proyectos"
                    value={overview.projects.length}
                    icon={<FaLaptopCode />}
                    tone="accent"
                    isLoading={isLoading}
                    footer={`${overview.visibleProjects} visibles · ${overview.featuredProjects} destacados`}
                />
                <MetricCard
                    label="Trayectoria"
                    value={overview.experience.length}
                    icon={<FaBriefcase />}
                    tone="neutral"
                    isLoading={isLoading}
                    footer={`${overview.visibleExperience} entradas publicadas`}
                />
                <MetricCard
                    label="Articulos"
                    value={overview.blog.length}
                    icon={<FaBookOpen />}
                    tone="accent"
                    isLoading={isLoading}
                    footer={`${overview.visibleBlog} visibles · ${overview.featuredPosts} destacados`}
                />
                <MetricCard
                    label="Redes"
                    value={overview.socials.length}
                    icon={<FaShareAlt />}
                    tone="neutral"
                    isLoading={isLoading}
                    footer={`${overview.visibleSocials} enlaces activos`}
                />
                <MetricCard
                    label="Stack"
                    value={overview.skills.length}
                    icon={<FaLayerGroup />}
                    tone="accent"
                    isLoading={isLoading}
                    footer={`${overview.visibleSkills} especialidades visibles`}
                />
                <MetricCard
                    label="Estadisticas"
                    value={overview.stats.length}
                    icon={<FaChartBar />}
                    tone="neutral"
                    isLoading={isLoading}
                    footer={`${overview.visibleStats} metricas publicadas`}
                />
            </MetricGrid>

            <div className="PBADMIN__dashboard_grid">
                <section className="PBADMIN__panel PBADMIN__panel--dashboard">
                    <div className="PBADMIN__panel_header">
                        <div>
                            <h2>Estado de publicacion</h2>
                            <p>Una lectura rapida de cuanto contenido esta actualmente visible en la web.</p>
                        </div>
                    </div>
                    <div className="PBADMIN__status_list">
                        <div className="PBADMIN__status_row">
                            <span>Proyectos visibles</span>
                            <strong>{overview.visibleProjects} / {overview.projects.length}</strong>
                        </div>
                        <div className="PBADMIN__status_row">
                            <span>Experiencias visibles</span>
                            <strong>{overview.visibleExperience} / {overview.experience.length}</strong>
                        </div>
                        <div className="PBADMIN__status_row">
                            <span>Articulos visibles</span>
                            <strong>{overview.visibleBlog} / {overview.blog.length}</strong>
                        </div>
                        <div className="PBADMIN__status_row">
                            <span>Redes activas</span>
                            <strong>{overview.visibleSocials} / {overview.socials.length}</strong>
                        </div>
                    </div>
                </section>

                <section className="PBADMIN__panel PBADMIN__panel--dashboard">
                    <div className="PBADMIN__panel_header">
                        <div>
                            <h2>Completitud del portfolio</h2>
                            <p>Control visual de los bloques esenciales antes de publicar.</p>
                        </div>
                    </div>
                    <div className="PBADMIN__progress_list">
                        <div className="PBADMIN__progress_item">
                            <div><span>Perfil publico</span><strong>{overview.profileScore}%</strong></div>
                            <div className="PBADMIN__progress_track"><span style={{ width: `${overview.profileScore}%` }} /></div>
                        </div>
                        <div className="PBADMIN__progress_item">
                            <div><span>Contenido publicado</span><strong>{overview.visibleProjects + overview.visibleBlog}</strong></div>
                            <div className="PBADMIN__progress_hint">Proyectos y articulos actualmente visibles.</div>
                        </div>
                    </div>
                </section>
            </div>
        </AdminViewLayout>
    );
};
